import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  Button,
  Icon,
  Avatar,
  Grid,
  GridItem,
  Spinner,
  Center,
  Tooltip,
  useToast,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiSearch,
  FiMail,
  FiPhone,
  FiStar,
  FiUser,
  FiMapPin,
  FiGift,
  FiBriefcase,
  FiClock,
} from 'react-icons/fi';
import moment from 'moment';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import {
  supabase,
  isSupabaseConfigured,
  transformContactToCamelCase,
} from '../../../utils/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useContactDrawer } from '../../../contexts/ContactDrawerContext';
import { useVisibilityScope } from '../../../hooks/useVisibilityScope';
import { canUserSeeRecord } from '../../../utils/recordVisibility';
import { useContactDeals } from '../../../hooks/useContactDeals';
import { FUNDED_STAGES } from '../../../constants/crmConstants';
import ContactTypeBadges from '../../../components/CRM/ContactTypeBadges';
import TagSelector from '../../../components/CRM/TagSelector';
import DncControl from '../../../components/CRM/DncControl';
import QuickAddDeal from '../../../components/CRM/QuickAddDeal';
import DealSwitcher from './DealSwitcher';
import DealIdentityCard from './DealIdentityCard';
import WorkPanel from './WorkPanel';
import DealRail from './DealRail';
import { recordRecentContact, sortDealsForDisplay } from './useWorkspaceSearch';
import { useAgentPage } from '../../../components/FutureBot/FutureBotProvider';
import { useWorkspaceAgentTools } from './useWorkspaceAgentTools';

/**
 * ============================================================================
 * PREVIEW — Person Workspace (/crm/workspace/:contactId)
 * ============================================================================
 *
 * The merged contact + deal working page. One place to work a PERSON:
 *  - identity header IS the contact surface: name (click → contact drawer),
 *    a spread-out info strip (email/phone/address/birthday/company/DNC and
 *    relationship chips), tags, and the deal switcher
 *  - the deal switcher handles 0, 1, or N deals ("two-pack" support)
 *  - WorkPanel logs activity through the exact same save paths as DealPage
 *  - two-column body: WorkPanel | DealRail (vitals, parties, Arive) — contact
 *    notes/campaigns/full editing live in the contact drawer, one click away
 *
 * Fully live: every write goes through an existing battle-tested path.
 * Zero changes to DealPage / DashboardPage / ContactDetailDrawer.
 * Allowlist-gated by WorkspacePreviewRoute.
 * ============================================================================
 */

/** Icon + text item in the header contact strip; renders nothing when empty */
function InfoChip({ icon, tooltip, onClick, hoverColor = 'purple.500', children }) {
  if (!children) return null;
  const inner = (
    <HStack
      spacing={1.5}
      minW={0}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      _hover={onClick ? { color: hoverColor } : undefined}
      transition="color 0.2s"
    >
      <Icon as={icon} boxSize={3.5} flexShrink={0} />
      <Text fontSize="sm" noOfLines={1} minW={0}>{children}</Text>
    </HStack>
  );
  return tooltip ? <Tooltip label={tooltip}>{inner}</Tooltip> : inner;
}

export default function PersonWorkspacePage() {
  const { contactId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const contactDrawer = useContactDrawer();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Unique instance ID to prevent Supabase channel collisions on re-mounts
  const instanceId = useRef(Math.random().toString(36).slice(2, 8));

  // Viewer's effective contact book (owned + team book-shares + record shares)
  // for the detail-page access check below.
  const { scope: viewerContactScope, loading: viewerScopeLoading } = useVisibilityScope({
    entityType: 'contact',
    targetUserId: currentUser?.uid,
  });

  /* ---------------- Contact: load + realtime ---------------- */
  const [contact, setContact] = useState(null);
  const [contactLoading, setContactLoading] = useState(true);

  // Detail-page visibility: URL knowledge must not grant access. Evaluated
  // reactively so a book share that resolves a moment later flips it open
  // without a refetch. Admins always pass.
  const canSeeContact = useMemo(() => {
    if (!contact) return true; // nothing to protect yet (loading / not found)
    return canUserSeeRecord({
      uid: currentUser?.uid,
      isAdmin: isCRMAdmin,
      record: contact,
      accessibleOwnerIds: viewerContactScope?.ownerIds,
    });
  }, [contact, currentUser?.uid, isCRMAdmin, viewerContactScope]);

  useEffect(() => {
    if (!contactId || !isSupabaseConfigured()) {
      setContactLoading(false);
      return undefined;
    }

    setContactLoading(true);
    const fetchContact = async () => {
      const { data, error } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('id', contactId)
        .single();

      if (error || !data) {
        console.error('Error loading contact:', error);
        toast({ title: 'Contact not found', status: 'error', duration: 4000 });
        history.push('/crm/workspace');
        return;
      }
      setContact(transformContactToCamelCase(data));
      setContactLoading(false);
      recordRecentContact(contactId);
    };

    fetchContact();

    const channel = supabase
      .channel(`ws-contact-${contactId}-${instanceId.current}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crm_contacts', filter: `id=eq.${contactId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            history.push('/crm/workspace');
          } else if (payload.new) {
            setContact(transformContactToCamelCase(payload.new));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contactId, history, toast]);

  /* ---------------- Deals (borrower + related party) ---------------- */
  const { asBorrower, asRelatedParty, loading: dealsLoading } = useContactDeals(contactId);
  const sortedDeals = useMemo(() => sortDealsForDisplay(asBorrower), [asBorrower]);

  // Everything selectable in the switcher: borrower deals first (open-first),
  // then deals this person is on as a related party (agent, co-borrower,
  // title...). Selecting a party deal works it right here — that's how you
  // click through every deal you share with e.g. an agent from their page.
  const selectableDeals = useMemo(
    () => [...sortedDeals, ...asRelatedParty],
    [sortedDeals, asRelatedParty]
  );
  const selectableDealIds = useMemo(() => selectableDeals.map((d) => d.id), [selectableDeals]);

  /* ---------------- Selected deal + ?deal= sync ---------------- */
  const queryDealId = useMemo(
    () => new URLSearchParams(location.search).get('deal'),
    [location.search]
  );
  const [selectedDealId, setSelectedDealId] = useState(queryDealId || null);
  // A deal created via "+ New Deal" is selected before useContactDeals'
  // realtime refetch delivers it — this ref stops the repair effect from
  // stomping that selection during the gap.
  const pendingDealSelectionRef = useRef(null);

  // Default/repair selection once deals load: honor ?deal= when valid (party
  // deals count), otherwise pick the first selectable deal — borrower deals
  // first (open-first), falling back to a party deal so e.g. an agent's page
  // opens straight onto a shared deal.
  useEffect(() => {
    if (dealsLoading) return;
    if (selectedDealId && selectableDealIds.includes(selectedDealId)) {
      if (pendingDealSelectionRef.current === selectedDealId) {
        pendingDealSelectionRef.current = null;
      }
      return;
    }
    if (selectedDealId && pendingDealSelectionRef.current === selectedDealId) return;
    setSelectedDealId(selectableDealIds[0] || null);
  }, [dealsLoading, selectableDealIds, selectedDealId]);

  const handleSelectDeal = (dealId, { pending = false } = {}) => {
    if (pending) pendingDealSelectionRef.current = dealId;
    setSelectedDealId(dealId);
    const params = new URLSearchParams(location.search);
    if (dealId) params.set('deal', dealId);
    else params.delete('deal');
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  /* ---------------- Instant-feedback deal patches ----------------
     Writes land in Supabase first; useContactDeals refetches via realtime
     within ~a second. Patches bridge that gap for the UI, then are cleared
     wholesale on the next refetch (which, because postgres_changes fires
     post-commit, is guaranteed to already contain our write). */
  const [dealPatches, setDealPatches] = useState({});
  useEffect(() => {
    setDealPatches({});
  }, [asBorrower, asRelatedParty]);

  const applyDealPatch = (dealId, patch) => {
    if (!dealId) return;
    setDealPatches((prev) => ({ ...prev, [dealId]: { ...(prev[dealId] || {}), ...patch } }));
  };

  const selectedDeal = useMemo(() => {
    const base = selectableDeals.find((d) => d.id === selectedDealId) || null;
    if (!base) return null;
    const patch = dealPatches[base.id];
    return patch ? { ...base, ...patch } : base;
  }, [selectableDeals, selectedDealId, dealPatches]);

  /* ---------------- FutureBot: share the live deal scenario ---------------- */
  useAgentPage(useWorkspaceAgentTools({ contact, selectedDeal }));

  /* ---------------- CRM users (owner names, task assignment) ---------------- */
  const [crmUsers, setCrmUsers] = useState([]);
  useEffect(() => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('CRMUser', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setCrmUsers(
        snapshot.docs.map((d) => ({
          uid: d.id,
          name: d.data().Name || d.data().name || d.data().email,
          ...d.data(),
        }))
      );
    });
    return () => unsubscribe();
  }, []);

  /* ---------------- Dialpad gate (same as DealPage/drawer) ---------------- */
  const [dialpadEnabled, setDialpadEnabled] = useState(false);
  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        setDialpadEnabled(userData.dialpadCapable === true && userData.dialpadEnabled === true);
      }
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  /* ---------------- Quick Add Deal (the "+ New Deal" chip) ---------------- */
  const { isOpen: isQuickAddDealOpen, onOpen: onQuickAddDealOpen, onClose: onQuickAddDealClose } = useDisclosure();

  /* ---------------- Favorite star (same write as the drawer) ---------------- */
  const [favoriteSaving, setFavoriteSaving] = useState(false);
  const handleToggleFavorite = async () => {
    if (!contact || favoriteSaving) return;
    const next = !contact.isFavorite;
    setFavoriteSaving(true);
    try {
      const { error } = await supabase
        .from('crm_contacts')
        .update({ is_favorite: next })
        .eq('id', contactId);
      if (error) throw error;
      setContact((prev) => ({ ...prev, isFavorite: next }));
    } catch (error) {
      toast({ title: 'Error updating favorite', description: error.message, status: 'error', duration: 4000 });
    } finally {
      setFavoriteSaving(false);
    }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text || '');
    toast({ title: `${label} copied`, status: 'success', duration: 2000 });
  };

  const ownerName =
    (contact?.ownerId && crmUsers.find((u) => u.uid === contact.ownerId)?.name) ||
    (contact?.ownerId ? null : 'Unassigned');

  /* ---------------- Styles ---------------- */
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const bannerBG = useColorModeValue('purple.600', 'purple.500');
  const headerBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  /* ---------------- Loading ---------------- */
  if (contactLoading || !contact) {
    return (
      <Box bg={bgColor} minH="100vh">
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="purple.500" thickness="4px" mb={4} />
          <Text color={mutedColor}>Loading person…</Text>
        </Center>
      </Box>
    );
  }

  /* ---------------- No access (graceful, no data leaked) ----------------
     While the viewer's book shares are still resolving, keep spinning instead
     of flashing a denial that a share would immediately reverse. */
  if (!canSeeContact) {
    if (viewerScopeLoading) {
      return (
        <Box bg={bgColor} minH="100vh">
          <Center h="60vh" flexDir="column">
            <Spinner size="xl" color="purple.500" thickness="4px" mb={4} />
            <Text color={mutedColor}>Loading person…</Text>
          </Center>
        </Box>
      );
    }
    return (
      <Box bg={bgColor} minH="100vh">
        <Center h="60vh" flexDir="column" px={6} textAlign="center">
          <Icon as={FiUser} boxSize={10} color={mutedColor} mb={4} />
          <Text fontSize="lg" fontWeight="bold" mb={1}>
            You don't have access to this contact
          </Text>
          <Text color={mutedColor} fontSize="sm" mb={6} maxW="420px">
            It belongs to another user's book. Ask them (or a CRM admin) to share
            it with you, or add you to their team.
          </Text>
          <Button leftIcon={<FiArrowLeft />} colorScheme="purple" onClick={() => history.push('/crm/workspace')}>
            Back to Workspace
          </Button>
        </Center>
      </Box>
    );
  }

  const contactFullName = `${contact.firstName || ''} ${contact.middleInitial ? `${contact.middleInitial}. ` : ''}${contact.lastName || ''}`.trim();

  /* Derived display values for the header contact strip */
  const mailing = contact.mailingAddress || {};
  const mailingText = (mailing.street || mailing.city)
    ? [mailing.street, [mailing.city, mailing.state].filter(Boolean).join(', '), mailing.zip]
        .filter(Boolean)
        .join(', ')
    : null;
  const companyText = [contact.jobTitle, contact.company].filter(Boolean).join(' @ ');
  const totalDeals = asBorrower.length + asRelatedParty.length;
  const fundedValue = asBorrower
    .filter((d) => FUNDED_STAGES.includes(d.stage) || d.stage === 'deal-won')
    .reduce((sum, d) => sum + (d.amount || 0), 0);

  return (
    <Box bg={bgColor} minH="100vh" pb={8} overflowX="clip">
      {/* PREVIEW banner (POC pattern) */}
      <Box bg={bannerBG} color="white" py={1.5} px={4} textAlign="center">
        <Text fontSize="xs" fontWeight="bold">
          PERSON WORKSPACE — PREVIEW · Live data, real saves · Same records as the classic pages
        </Text>
      </Box>

      {/* Sticky identity header + deal switcher */}
      <Box
        bg={headerBG}
        pt={{ base: 3, md: 4 }}
        pb={{ base: 2, md: 3 }}
        px={{ base: 3, md: 6, lg: 8 }}
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={100}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        <Box maxW="1600px" mx="auto">
          <Grid
            templateColumns={{
              base: 'minmax(0, 1fr) auto',
              lg: 'minmax(0, 1.7fr) minmax(0, 1.4fr) minmax(0, 1fr) auto',
            }}
            gap={{ base: 2, lg: 4 }}
            alignItems="center"
          >
            {/* Identity — click the name (or avatar) to open the contact drawer */}
            <HStack
              spacing={{ base: 2, md: 3 }}
              minW={0}
              flexWrap="wrap"
              rowGap={1}
              gridColumn="1"
              gridRow="1"
            >
              <Tooltip label="Back to person search">
                <IconButton
                  icon={<FiArrowLeft />}
                  aria-label="Back to person search"
                  onClick={() => history.push('/crm/workspace')}
                  variant="ghost"
                  size="md"
                />
              </Tooltip>
              <Tooltip label="Open contact details">
                <HStack
                  spacing={{ base: 2, md: 3 }}
                  minW={0}
                  cursor="pointer"
                  role="group"
                  onClick={() => contactDrawer?.openContactDrawer(contactId)}
                >
                  <Avatar size="md" name={contactFullName} display={{ base: 'none', md: 'flex' }} />
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: 'md', md: 'lg' }}
                    noOfLines={1}
                    minW={0}
                    _groupHover={{ color: 'purple.500', textDecoration: 'underline' }}
                    transition="color 0.2s"
                  >
                    {contactFullName}
                  </Text>
                </HStack>
              </Tooltip>
              <Tooltip label={contact.isFavorite ? 'Remove favorite' : 'Mark favorite'}>
                <IconButton
                  aria-label="Toggle favorite"
                  icon={<Icon as={FiStar} fill={contact.isFavorite ? 'currentcolor' : 'none'} />}
                  size="xs"
                  variant="ghost"
                  color={contact.isFavorite ? 'yellow.400' : 'gray.400'}
                  isLoading={favoriteSaving}
                  onClick={handleToggleFavorite}
                />
              </Tooltip>
              <ContactTypeBadges
                contactTypes={contact.contactTypes}
                primaryContactType={contact.primaryContactType}
                maxDisplay={2}
              />
              {(isCRMAdmin || (contact?.ownerId && contact.ownerId !== currentUser?.uid)) && ownerName && (
                <Badge colorScheme="purple" fontSize="2xs" maxW="180px" isTruncated>
                  {ownerName}
                </Badge>
              )}
            </HStack>

            {/* Actions */}
            <HStack
              spacing={2}
              justify="flex-end"
              gridColumn={{ base: '2', lg: '4' }}
              gridRow="1"
            >
              <Tooltip label="Find another person">
                <IconButton
                  aria-label="Find another person"
                  icon={<FiSearch />}
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  onClick={() => history.push('/crm/workspace')}
                />
              </Tooltip>
              <Button
                size="sm"
                leftIcon={<FiUser />}
                variant="outline"
                onClick={() => contactDrawer?.openContactDrawer(contactId)}
                display={{ base: 'none', md: 'inline-flex' }}
              >
                Open Contact
              </Button>
              <IconButton
                aria-label="Open contact"
                icon={<FiUser />}
                size="sm"
                variant="outline"
                onClick={() => contactDrawer?.openContactDrawer(contactId)}
                display={{ base: 'inline-flex', md: 'none' }}
              />
            </HStack>

            {/* Deal switcher — front and center between identity and tags on
                desktop; its own full-width row on mobile */}
            <Box
              gridColumn={{ base: '1 / -1', lg: '2' }}
              gridRow={{ base: 'auto', lg: '1' }}
              alignSelf="center"
              minW={0}
            >
              <DealSwitcher
                compact
                deals={sortedDeals}
                relatedPartyDeals={asRelatedParty}
                selectedDealId={selectedDealId}
                loading={dealsLoading}
                onSelect={handleSelectDeal}
                onNewDeal={onQuickAddDealOpen}
              />
            </Box>

            {/* Tags (contact-level; DB mirrors onto deals) — own row on mobile */}
            <VStack
              align={{ base: 'stretch', lg: 'center' }}
              justify="center"
              spacing={1}
              minW={0}
              gridColumn={{ base: '1 / -1', lg: '3' }}
              gridRow={{ base: 'auto', lg: '1' }}
            >
              <TagSelector
                contactId={contactId}
                currentTags={contact.tags || []}
                userId={currentUser?.uid}
                isEditable={true}
                onTagsChange={(newTags) => setContact((prev) => ({ ...prev, tags: newTags }))}
              />
            </VStack>

            {/* Contact info strip — the whole-person surface that replaced the
                old left rail; full details/notes/campaigns live in the drawer */}
            <HStack
              gridColumn="1 / -1"
              spacing={{ base: 3, md: 5 }}
              rowGap={1.5}
              flexWrap="wrap"
              color={mutedColor}
              minW={0}
            >
              <InfoChip
                icon={FiMail}
                tooltip="Copy email"
                onClick={() => handleCopy(contact.email, 'Email')}
              >
                {contact.email}
              </InfoChip>
              <InfoChip
                icon={FiPhone}
                tooltip="Copy phone"
                hoverColor="blue.500"
                onClick={() => handleCopy(contact.phone, 'Phone')}
              >
                {contact.phone}
              </InfoChip>
              <InfoChip
                icon={FiMapPin}
                tooltip="Copy address"
                onClick={() => handleCopy(mailingText, 'Address')}
              >
                {mailingText}
              </InfoChip>
              <InfoChip icon={FiGift} tooltip="Birthday">
                {contact.birthday ? moment(contact.birthday).format('MMM D') : null}
              </InfoChip>
              <InfoChip icon={FiBriefcase} tooltip="Company">
                {companyText}
              </InfoChip>
              <InfoChip icon={FiClock}>
                {contact.createdAt ? `Since ${moment(contact.createdAt).format('MMM YYYY')}` : null}
              </InfoChip>
              <DncControl
                contactId={contactId}
                contact={contact}
                onChanged={(next) => setContact((c) => ({ ...c, ...next }))}
              />
              {totalDeals > 0 && (
                <Tooltip label={`${asBorrower.length} as borrower · ${asRelatedParty.length} as party`}>
                  <Badge colorScheme="blue" variant="subtle" flexShrink={0}>
                    {totalDeals} {totalDeals === 1 ? 'deal' : 'deals'}
                  </Badge>
                </Tooltip>
              )}
              {fundedValue > 0 && (
                <Badge colorScheme="green" variant="subtle" flexShrink={0}>
                  ${(fundedValue / 1000).toFixed(0)}K funded
                </Badge>
              )}
            </HStack>
          </Grid>
        </Box>
      </Box>

      {/* 2-column body: Work | Deal (contact lives in the header + drawer) */}
      <Container maxW="1600px" py={{ base: 4, md: 6 }} px={{ base: 2, md: 6 }}>
        <Grid
          templateColumns={{
            base: 'minmax(0, 1fr)',
            lg: 'minmax(0, 1.8fr) minmax(0, 1.1fr)',
          }}
          gap={{ base: 4, lg: 5 }}
          alignItems="start"
        >
          {/* The working area (first on mobile) */}
          <GridItem minW={0}>
            <VStack align="stretch" spacing={4}>
              {/* Who/what is this deal? Borrower, loan #, address, amount —
                  always in view above the work area. Critical on party deals
                  (realtor selected → borrower shown right here). */}
              {selectedDeal && <DealIdentityCard deal={selectedDeal} contact={contact} />}
              {/* deals/dealIds include party deals so the unified timeline, task
                  scope, and the log modal's deal picker cover every deal this
                  person touches — not just the ones they borrow on */}
              <WorkPanel
                contact={contact}
                deal={selectedDeal}
                deals={selectableDeals}
                dealIds={selectableDealIds}
                crmUsers={crmUsers}
                dialpadEnabled={dialpadEnabled}
                onDealPatched={applyDealPatch}
                onCreateDeal={onQuickAddDealOpen}
              />
            </VStack>
          </GridItem>

          {/* Right: the selected deal (second on mobile) */}
          <GridItem
            minW={0}
            position={{ base: 'relative', lg: 'sticky' }}
            top={{ base: 'auto', lg: '180px' }}
            alignSelf="start"
            maxHeight={{ base: 'none', lg: 'calc(100vh - 200px)' }}
            overflowY={{ base: 'visible', lg: 'auto' }}
          >
            <DealRail
              contact={contact}
              deal={selectedDeal}
              dialpadEnabled={dialpadEnabled}
              onCreateDeal={onQuickAddDealOpen}
            />
          </GridItem>
        </Grid>
      </Container>

      {/* "+ New Deal" — existing QuickAddDeal, stay in the workspace and
          select the new deal in place (openAfterCreate=false) */}
      <QuickAddDeal
        isOpen={isQuickAddDealOpen}
        onClose={onQuickAddDealClose}
        contact={contact}
        openAfterCreate={false}
        onDealCreated={(newDeal) => {
          if (newDeal?.id) handleSelectDeal(newDeal.id, { pending: true });
        }}
      />
    </Box>
  );
}
